import { Nfc, Palette, CreditCard, RefreshCw, Layers, Smartphone } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { CtaWhatsApp } from "../components/Cta";
import { Reveal, Tilt, Marquee } from "../components/motion";
import { CardPair } from "../components/CardMockup";
import { PRICE_TEASER, DELIVERY_DELAY, RESPONSE_TIME } from "../config/site";
import { GALLERY_DESIGNS } from "../data/gallery";
import { gradientFrom, readableOn } from "../lib/colors";

const SPECS = [
  { icon: CreditCard, t: "Format carte bancaire", d: "86 × 54 mm en PVC premium : le format qui se glisse partout — comptoir, porte-cartes, poche de veste — et qui dure des années." },
  { icon: Nfc, t: "Puce NFC intégrée", d: "Un geste du smartphone suffit : la page digitale s'ouvre instantanément, sans application ni réglage, sur iPhone et Android." },
  { icon: Smartphone, t: "Compatible iPhone & Android", d: "Tous les smartphones récents lisent le NFC nativement : aucune application à installer, ni pour vous ni pour vos clients." },
  { icon: Palette, t: "Design 100 % personnalisé", d: "Logo, nom, couleurs, slogan, identité visuelle : la carte est conçue comme un objet de marque, pas comme un gadget." },
  { icon: Layers, t: "Une carte, tout l'univers", d: "Avis Google, réseaux sociaux, menu, contact : tout est regroupé derrière un seul objet élégant." },
  { icon: RefreshCw, t: "Contenu modifiable", d: "Vos liens évoluent ? Votre page digitale aussi — sans remplacer la carte. Un message sur WhatsApp suffit." },
];

export default function LaCarte() {
  return (
    <>
      <Seo
        title="La carte digitale premium"
        description="Carte PVC 86 × 54 mm personnalisée équipée NFC : la porte d'entrée vers votre univers digital. Design à votre image, à partir de 40 € TTC."
        path="/la-carte"
      />

      <PageHero
        dark
        eyebrow="La carte"
        titleLines={["Un objet premium.", "Un monde derrière."]}
        description="Chez Noveocard, la carte n'est jamais le produit final : c'est la porte d'entrée vers l'univers digital de votre entreprise. Le NFC n'est que la clé."
      >
        <CtaWhatsApp showResponseTime testid="carte-hero-cta" />
      </PageHero>

      {/* Spotlight produit */}
      <section className="grain relative overflow-hidden bg-nuit-950 py-20 text-white sm:py-28">
        <div className="container-nv grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="relative mx-auto max-w-xl">
              <div className="absolute -inset-14 rounded-full bg-electric/20 blur-3xl" aria-hidden="true" />
              <div className="relative flex items-center gap-5" style={{ perspective: "1200px" }}>
                <Tilt max={9} className="w-1/2 -rotate-3">
                  <img
                    src="/assets/ref-recto.webp"
                    alt="Carte Noveocard recto : bleu nuit lumineux, logo N et slogan « Un simple geste ouvre un monde »"
                    width="1600"
                    height="1004"
                    className="w-full rounded-2xl shadow-[0_36px_70px_-28px_rgba(0,0,0,0.75)]"
                  />
                </Tilt>
                <Tilt max={9} className="w-1/2 rotate-3 translate-y-4">
                  <img
                    src="/assets/ref-verso.webp"
                    alt="Carte Noveocard verso : « Approchez votre téléphone » avec accès WhatsApp, site internet, avis Google, Instagram et TikTok"
                    width="1600"
                    height="1004"
                    className="w-full rounded-2xl shadow-[0_36px_70px_-28px_rgba(0,0,0,0.75)]"
                  />
                </Tilt>
              </div>
              <p className="mt-8 text-center text-xs font-medium uppercase tracking-[0.2em] text-white/40">
                PVC 86 × 54 mm · NFC · finition premium recto & verso
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow text-electric-300">L'objet</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-5xl">
              Penser carte. Penser surtout expérience.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-white/70 sm:text-lg">
              Nous ne vendons pas un morceau de plastique avec une puce. Nous créons l'objet qui
              déclenche la rencontre entre vos clients et votre univers digital : vos avis, vos
              réseaux, vos contenus, votre image.
            </p>
            <p className="mt-4 text-base leading-relaxed text-white/70 sm:text-lg">
              Un seul matériau — le PVC premium — parce que c'est celui qui offre la meilleure
              qualité d'impression, la meilleure durabilité et le format le plus naturel du quotidien.
            </p>
            <p className="mt-4 rounded-2xl border border-electric/30 bg-electric/10 p-4 text-sm leading-relaxed text-electric-200">
              Votre carte ne se limite pas à un logo sur une couleur : chaque carte est dessinée
              sur-mesure (cadres, ornements, dégradés, icônes, verso complet), dans la couleur de votre
              choix. <strong className="text-white">Personnalisation sur-mesure incluse, validée avec
              vous avant production.</strong>
            </p>
            <div className="mt-8 flex flex-col items-start gap-3">
              <CtaWhatsApp testid="carte-spotlight-cta" />
              <p className="text-sm text-white/55">{PRICE_TEASER} · {DELIVERY_DELAY}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Caractéristiques */}
      <section className="bg-white py-20 sm:py-28">
        <div className="container-nv">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Caractéristiques</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-5xl">
              Simple à l'œil. Sérieuse dans les détails.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SPECS.map((s, i) => (
              <Reveal key={s.t} delay={(i % 3) * 0.08}>
                <article className="h-full rounded-3xl border border-nuit-900/10 bg-brume p-7 transition-colors duration-500 hover:bg-white hover:shadow-[0_24px_50px_-24px_rgba(7,26,45,0.3)]">
                  <s.icon className="h-6 w-6 text-electric" />
                  <h3 className="mt-4 font-display text-lg font-bold text-nuit-900">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-steel">{s.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Variantes de style */}
      <section className="bg-brume py-16 sm:py-24">
        <div className="container-nv">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">À vos couleurs</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-nuit-900 sm:text-4xl">
              Votre identité dicte le design. Jamais l'inverse.
            </h2>
            <p className="mt-4 text-steel">
              Quelques directions possibles — exemples de démonstration. Le design final est construit
              à partir de votre identité visuelle.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-10 lg:grid-cols-2">
            {GALLERY_DESIGNS.slice(0, 4).map((d, i) => (
              <Reveal key={d.id} delay={(i % 2) * 0.08}>
                <div className="rounded-3xl bg-nuit-950 px-5 py-8 transition-transform duration-500 hover:-translate-y-1.5">
                  <CardPair
                    name={d.name}
                    bg={gradientFrom(d.base)}
                    fg={readableOn(d.base)}
                    accent={d.accent}
                    pattern={d.pattern || null}
                    patternColor={d.accent}
                  />
                </div>
                <p className="mt-4 text-center text-sm text-steel">
                  {d.style} — exemple de démonstration
                </p>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="mt-14 border-t border-nuit-900/10 pt-8">
          <Marquee
            items={["Logo", "Nom", "Couleurs", "Slogan", "Identité visuelle", "Design sur mesure"]}
            itemClassName="font-display text-sm font-semibold uppercase tracking-[0.25em] text-nuit-900/40"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="grain relative overflow-hidden bg-nuit-900 py-20 text-center text-white sm:py-24">
        <div className="container-nv relative">
          <Reveal>
            <p className="font-display text-3xl font-bold tracking-tight sm:text-5xl">
              Prêt à ouvrir votre monde ?
            </p>
            <p className="mx-auto mt-4 max-w-lg text-white/70">
              {PRICE_TEASER}. {RESPONSE_TIME} sur WhatsApp.
            </p>
            <CtaWhatsApp className="mt-8" size="lg" testid="carte-bottom-cta" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
